import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, getToken } from '../api/client';
import { PhotosEdl, type PhotoPrise } from '../components/PhotosEdl';
import { dataUrlEnBlob, envoyerPhotos } from '../lib/photos';
import { effacerBrouillon, enregistrerBrouillon, lireBrouillon, mettreEnFile } from '../lib/offline';
import { Bandeau, Carte, Champ, Squelette } from '../components/ui';
import { SchemaVoiture, Signature, type Dommage } from '../components/SchemaVoiture';
import { centimes, dateFr, dateHeureFr, deviseReference, montant, nomClient } from '../lib/format';
import { T, langue, reglagesRegionaux } from '../i18n';
import type { Client, Contrat, LigneSupplement, Reservation, Vehicule } from '../api/types';

const CLES_ACCESSOIRES = ['cle', 'carteGrise', 'assurance', 'vignette', 'controleTechnique', 'giletTriangle', 'crick', 'roueSecours'];
/** Fonction : le libellé suit la langue active. */
const ACCESSOIRES = (): [string, string][] => CLES_ACCESSOIRES.map((k) => [k, T.etatDesLieux.accessoires[k] ?? k]);
const TYPES = ['rayure', 'choc', 'eclat', 'manquant'];

/**
 * Les memes controles que sur leur contrat papier : carburant en QUARTS,
 * proprete notee 10/10 · 5/10 · 0/10, et les huit accessoires — ni plus, ni moins.
 */
/** Les quatre suppléments que le contrat impose ; cote serveur : CODES_ATTENDUS. */
const LIBELLE_SUPPLEMENT = (): Record<string, string> => T.etatDesLieux.supplements;

/** Ce qui doit survivre a un rechargement : tout ce que l'agent a saisi dehors. */
interface SaisieEnCours {
  etape: number; km: string; quarts: number; propInt: number; propExt: number;
  acc: Record<string, boolean>; dommages: Dommage[]; photos: PhotoPrise[];
  observations: string; sigClient: string; sigAgent: string;
}

export default function EtatDesLieux() {
  const { id, sens } = useParams();
  const nav = useNavigate();
  const retour = sens === 'retour';
  const L = T.etatDesLieux;
  const u = reglagesRegionaux().uniteDistance;
  const nb = (x: number) => x.toLocaleString(langue === 'fr' ? 'fr-FR' : 'en-US');
  const CARBURANT_LABEL = L.carburantQuarts;

  const [contrat, setContrat] = useState<Contrat | null>(null);
  const [etape, setEtape] = useState(1);
  const [km, setKm] = useState('');
  const [quarts, setQuarts] = useState(4);
  const [propInt, setPropInt] = useState(10);
  const [propExt, setPropExt] = useState(10);
  const [acc, setAcc] = useState<Record<string, boolean>>(Object.fromEntries(CLES_ACCESSOIRES.map((k) => [k, true])));
  const [dommages, setDommages] = useState<Dommage[]>([]);
  const [photos, setPhotos] = useState<PhotoPrise[]>([]);
  const [horsLigne, setHorsLigne] = useState(false);
  const [nouveau, setNouveau] = useState<Dommage | null>(null);
  const [observations, setObservations] = useState('');
  /*
   * Le moment REEL du départ ou du retour.
   *
   * L'écran envoyait `new Date()` : l'heure de la SAISIE, pas celle où la
   * voiture est partie. Une voiture qui sort à 9h et qu'on enregistre à 11h
   * était notée à 11h — et c'est cette heure-là qui sert à calculer un retard.
   */
  const [moment, setMoment] = useState(() => {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  });
  /* Le prix d'une heure de retard (article 02), pour l'annoncer AVANT de valider. */
  const [prixRetard, setPrixRetard] = useState(0);
  useEffect(() => {
    api.get<{ code: string; prixMinor: number }[]>('/supplements')
      .then((l) => setPrixRetard(l.find((x) => x.code === 'retard')?.prixMinor ?? 0)).catch(() => {});
  }, []);
  const [sigClient, setSigClient] = useState('');
  const [sigAgent, setSigAgent] = useState('');
  const [resultat, setResultat] = useState<{
    supplements: LigneSupplement[]; nouveauxDommages: Dommage[]; codesManquants?: string[];
    /* Rendu avant la fin prévue : ce que le logiciel propose de rendre au client. */
    retourAnticipe?: { joursRendus: number; finPrevue: string; encaisseMinor: number; montantProposeMinor: number } | null;
  } | null>(null);
  /*
   * RETOUR AVANT LA FIN : ON PROPOSE DE RENDRE L'ARGENT.
   *
   * « Quand on arrête une location avant sa fin, on a un popup pour redonner
   * l'argent au locataire. » Le logiciel ne décide pas : il compte les jours
   * rendus, propose la somme, et le comptoir tranche. Sans cette fenêtre, le
   * trop-perçu restait en caisse sans que personne ne s'en aperçoive.
   */
  const [rembourse, setRembourse] = useState('');
  const [motifRemb, setMotifRemb] = useState('');
  const [rembFait, setRembFait] = useState('');
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [progres, setProgres] = useState('');
  const [sansPhotoAssume, setSansPhotoAssume] = useState(false);
  const [brouillonPropose, setBrouillonPropose] = useState<SaisieEnCours | null>(null);
  const [brouillonCharge, setBrouillonCharge] = useState(false);
  const cleBrouillon = `edl:${id}:${sens}`;

  // Un brouillon existant est PROPOSE, jamais applique d'office : l'agent doit
  // savoir qu'il reprend une saisie, et pouvoir repartir de zero s'il prefere.
  useEffect(() => {
    lireBrouillon<SaisieEnCours>(cleBrouillon)
      .then((b) => { if (b?.donnees) setBrouillonPropose(b.donnees); })
      .catch(() => {})
      .finally(() => setBrouillonCharge(true));
  }, [cleBrouillon]);

  // Ecriture continue, une fois le brouillon eventuel traite.
  useEffect(() => {
    if (!brouillonCharge || brouillonPropose || etape >= 9) return;
    const vide = !km && dommages.length === 0 && photos.length === 0 && !observations && !sigClient && !sigAgent;
    if (vide && etape === 1) return;
    const t = setTimeout(() => {
      void enregistrerBrouillon<SaisieEnCours>(cleBrouillon, {
        etape, km, quarts, propInt, propExt, acc, dommages, photos, observations, sigClient, sigAgent,
      }).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [brouillonCharge, brouillonPropose, cleBrouillon, etape, km, quarts, propInt, propExt, acc, dommages, photos, observations, sigClient, sigAgent]);

  // Filet supplementaire : le navigateur previent avant de fermer l'onglet.
  useEffect(() => {
    if (etape >= 9) return;
    const rien = photos.length === 0 && dommages.length === 0 && !sigClient && !sigAgent;
    if (rien) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [etape, photos.length, dommages.length, sigClient, sigAgent]);

  function reprendre(b: SaisieEnCours) {
    setEtape(b.etape); setKm(b.km); setQuarts(b.quarts);
    setPropInt(b.propInt); setPropExt(b.propExt); setAcc(b.acc);
    setDommages(b.dommages); setPhotos(b.photos); setObservations(b.observations);
    setSigClient(b.sigClient); setSigAgent(b.sigAgent);
    setBrouillonPropose(null);
  }

  function repartirDeZero() {
    void effacerBrouillon(cleBrouillon).catch(() => {});
    setBrouillonPropose(null);
  }

  useEffect(() => {
    api.get<{ contrat: Contrat }>(`/contrats/${id}`).then((d) => {
      setContrat(d.contrat);
      const v = d.contrat.vehicle as Vehicule;
      setKm(String(retour ? (d.contrat.remise?.km ?? v.km) : v.km));
    });
  }, [id, retour]);

  if (!contrat) return <Squelette lignes={6} />;
  const v = contrat.vehicle as Vehicule;
  const c = contrat.customer as Client;
  const depart = contrat.remise;

  async function valider() {
    setErreur(''); setEnvoi(true);
    try {
      const base = {
        dateHeure: new Date(moment).toISOString(),
        km: Number(km), carburantQuarts: quarts,
        propreteInterieur: propInt, propreteExterieur: propExt,
        accessoires: acc,
        dommages: dommages.map(({ type, zone, x, y, commentaire }) => ({ type, zone, x, y, commentaire })),
        observations, signatureClient: sigClient, signatureAgent: sigAgent,
      };

      // Hors ligne : tout part dans la file, photos comprises, et repart seul au retour du reseau.
      if (!navigator.onLine) {
        await mettreEnFile(
          `/contrats/${id}/${retour ? 'restitution' : 'remise'}`,
          { ...base, photos: photos.map((p) => p.dataUrl) },
          L.libelleFile(retour, contrat!.numero),
        );
        await effacerBrouillon(cleBrouillon).catch(() => {});
        setHorsLigne(true); setEtape(9);
        return;
      }

      const urls = await envoyerPhotos(
        await Promise.all(photos.map((p) => dataUrlEnBlob(p.dataUrl))),
        getToken(),
        (faites, total) => setProgres(L.envoiPhotos(faites, total)),
      );
      setProgres('');
      const corps = { ...base, photos: urls };
      if (retour) {
        const r = await api.post<{
    supplements: LigneSupplement[]; nouveauxDommages: Dommage[]; codesManquants?: string[];
    /* Rendu avant la fin prévue : ce que le logiciel propose de rendre au client. */
    retourAnticipe?: { joursRendus: number; finPrevue: string; encaisseMinor: number; montantProposeMinor: number } | null;
  }>(`/contrats/${id}/restitution`, corps);
        await effacerBrouillon(cleBrouillon).catch(() => {});
        setResultat(r); setEtape(9);
        if (r.retourAnticipe) setRembourse(String(r.retourAnticipe.montantProposeMinor / 100));
      } else {
        await api.post(`/contrats/${id}/remise`, corps);
        await effacerBrouillon(cleBrouillon).catch(() => {});
        nav(`/reservations/${typeof contrat!.reservation === 'string' ? contrat!.reservation : contrat!.reservation._id}`);
      }
    } catch (e) {
      setErreur(e instanceof ApiError ? e.erreur : L.erreurEnregistrement);
    } finally { setEnvoi(false); }
  }

  const totalExtras = resultat?.supplements.reduce((a, l) => a + l.totalMinor, 0) ?? 0;

  /** Rendre l'argent des jours non utilises : la caisse baisse, le contrat suit. */
  async function rendreArgent() {
    try {
      await api.post(`/contrats/${id}/rembourser`, {
        montantMinor: centimes(Number(rembourse)), motif: motifRemb,
      });
      setRembFait(L.rendus(montant(centimes(Number(rembourse)))));
    } catch (e) { setErreur(e instanceof ApiError ? e.erreur : L.erreurRemboursement); }
  }

  if (etape === 9 && horsLigne) {
    return (
      <>
        <div className="entete"><div className="titres"><h1>{L.horsLigneTitre}</h1><p>{contrat.numero} · {v.marque} {v.modele}</p></div></div>
        <Bandeau type="warn" titre={L.fileTitre}>
          {L.horsLigneTexte(photos.length)}
        </Bandeau>
        <div className="actions" style={{ marginTop: 16 }}>
          <button className="btn btn-p btn-l" onClick={() => nav('/')}>{L.revenirTaches}</button>
        </div>
      </>
    );
  }

  if (etape === 9 && resultat) {
    return (
      <>
        <div className="entete"><div className="titres"><h1>{L.retourEnregistre}</h1><p>{contrat.numero} · {v.marque} {v.modele}</p></div></div>
        {resultat.nouveauxDommages.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <Bandeau type="ko" titre={L.nouveauxDommages(resultat.nouveauxDommages.length)}>
              {L.nouveauxDommagesTexte}
            </Bandeau>
          </div>
        )}
        <Carte titre={L.supplementsAFacturer} pad={false}>
          {resultat.codesManquants && resultat.codesManquants.length > 0 ? (
            /*
             * Le serveur signale que des suppléments du contrat ne sont pas au
             * catalogue. Sans eux, le calcul est partiel — et on est exactement
             * a l'endroit ou se decide la restitution de la caution. Dire
             * « conforme » ici serait un mensonge couteux.
             */
            <div className="carte-p">
              <Bandeau type="ko" titre={L.nonChiffrable}>
                {L.absentsCatalogue}
                <ul style={{ margin: '8px 0 0 18px' }}>
                  {resultat.codesManquants.map((c) => <li key={c}>{LIBELLE_SUPPLEMENT()[c] ?? c}</li>)}
                </ul>
                {L.gerantDoitA}<b>{L.parametresSupplements}</b>{L.gerantDoitB}
                {totalExtras > 0 && <>{L.totalIncompletA}<b>{L.incomplet}</b>{L.totalIncompletB}</>}
              </Bandeau>
            </div>
          ) : null}
          {resultat.supplements.length === 0 && !(resultat.codesManquants?.length) ? (
            <div className="carte-p"><Bandeau type="ok" titre={L.aucunSupplement}>{L.conforme}</Bandeau></div>
          ) : (
            <table className="tbl">
              <thead><tr><th>{L.colSupplement}</th><th>{L.colCalcul}</th><th className="droite">{L.colMontant}</th></tr></thead>
              <tbody>
                {resultat.supplements.map((l) => (
                  <tr key={l.code}>
                    <td><b>{l.libelle}</b></td>
                    <td className="mini sourdine">{l.formule}</td>
                    <td className="droite montant">{montant(l.totalMinor)}</td>
                  </tr>
                ))}
                <tr><td colSpan={2}><b>{L.totalSupplements}</b></td><td className="droite montant"><b>{montant(totalExtras)}</b></td></tr>
              </tbody>
            </table>
          )}
        </Carte>
        {resultat.retourAnticipe && !rembFait && (
          <div style={{ marginTop: 16 }}>
            <Carte titre={L.retourAnticipe(resultat.retourAnticipe.joursRendus)}>
              <Bandeau type="warn" titre={L.finPrevue(dateFr(resultat.retourAnticipe.finPrevue))}>
                {L.verseA(montant(resultat.retourAnticipe.encaisseMinor))}{' '}
                <b>{montant(resultat.retourAnticipe.montantProposeMinor)}</b>{L.verseB}
              </Bandeau>
              <div className="grille g2" style={{ marginTop: 12 }}>
                <Champ label={L.montantARendre(deviseReference())}>
                  <input type="number" min={0} step={500} value={rembourse}
                         onChange={(e) => setRembourse(e.target.value)} />
                </Champ>
                <Champ label={L.motifObligatoire}>
                  <input value={motifRemb} onChange={(e) => setMotifRemb(e.target.value)}
                         placeholder={L.motifPlaceholder} />
                </Champ>
              </div>
              <div className="actions" style={{ marginTop: 12 }}>
                <button className="btn btn-p"
                        disabled={!(Number(rembourse) > 0) || motifRemb.trim().length < 3}
                        onClick={() => void rendreArgent()}>
                  {L.rendre(Number(rembourse) > 0 ? montant(centimes(Number(rembourse))) : L.largent)}
                </button>
                <button className="btn" onClick={() => setRembFait(L.aucunRemboursement)}>
                  {L.neRienRendre}
                </button>
              </div>
            </Carte>
          </div>
        )}
        {rembFait && (
          <div style={{ marginTop: 16 }}><Bandeau type="ok" titre={L.anticipeTraite}>{rembFait}</Bandeau></div>
        )}

        <div className="actions" style={{ marginTop: 16 }}>
          <button className="btn btn-p btn-l" onClick={() => nav(`/reservations/${typeof contrat.reservation === 'string' ? contrat.reservation : contrat.reservation._id}`)}>
            {L.voirDossier}
          </button>
          <button className="btn" onClick={() => nav(`/caisse?contrat=${contrat._id}`)}>{L.encaisserSolde}</button>
        </div>
      </>
    );
  }

  if (brouillonPropose) {
    const b = brouillonPropose;
    return (
      <>
        <div className="entete">
          <div className="titres">
            <h1>État des lieux — {retour ? 'retour' : 'départ'}</h1>
            <p>{contrat.numero} · {v.marque} {v.modele} · {v.immatriculation} — {nomClient(c)}</p>
          </div>
        </div>
        <Bandeau type="warn" titre={L.saisieEnCours}>
          {L.etiezA}<b>{L.etapeSur(b.etape)}</b>
          {b.photos.length > 0 && <>{L.avec}<b>{L.photosN(b.photos.length)}</b></>}
          {b.dommages.length > 0 && <>{L.virgule}<b>{L.dommagesN(b.dommages.length)}</b>{L.releves}</>}
          {(b.sigClient || b.sigAgent) && <>{L.et}<b>{[b.sigClient && L.laSigClient, b.sigAgent && L.celleAgent].filter(Boolean).join(L.joinEt)}</b></>}{L.point}
          <div className="actions" style={{ marginTop: 14 }}>
            <button className="btn btn-p btn-l" onClick={() => reprendre(b)}>{L.reprendre}</button>
            <button className="btn" onClick={repartirDeZero}>{L.recommencer}</button>
          </div>
        </Bandeau>
      </>
    );
  }

  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>État des lieux — {retour ? 'retour' : 'départ'}</h1>
          <p>{contrat.numero} · {v.marque} {v.modele} · {v.immatriculation} — {nomClient(c)}</p>
        </div>
        <div className="mini sourdine">{L.etape(etape)}</div>
      </div>

      {retour && depart && (
        <div style={{ marginBottom: 14 }}>
          <Bandeau type="info" titre={L.auDepart}>
            {L.resumeDepart(dateHeureFr(depart.dateHeure), nb(depart.km), u, CARBURANT_LABEL[depart.carburantQuarts] ?? '',
              depart.propreteInterieur, depart.propreteExterieur, depart.dommages.length)}
          </Bandeau>
        </div>
      )}
      {erreur && <div style={{ marginBottom: 14 }}><Bandeau type="ko">{erreur}</Bandeau></div>}

      <div className="grille g2" style={{ alignItems: 'start' }}>
        <div style={{ display: 'grid', gap: 14 }}>
          {etape === 1 && (
            <Carte titre={L.photos}>
              <PhotosEdl photos={photos} onChange={setPhotos} />
            </Carte>
          )}

          {etape === 2 && (
            <Carte titre={L.dommages}>
              <SchemaVoiture
                dommages={dommages}
                anciens={retour ? (depart?.dommages ?? []) : []}
                onAjouter={(x, y) => setNouveau({ type: 'rayure', zone: y < .3 ? 'avant' : y > .75 ? 'arriere' : x < .5 ? 'cote_gauche' : 'cote_droit', x, y })}
              />
              {retour && (depart?.dommages.length ?? 0) > 0 && (
                <p className="mini sourdine" style={{ marginTop: 8 }}>{L.pointsGris}</p>
              )}
              {nouveau && (
                <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
                  <Champ label={L.typeDommage}>
                    <div className="choix">
                      {TYPES.map((t) => (
                        <button key={t} type="button" aria-pressed={nouveau.type === t} onClick={() => setNouveau({ ...nouveau, type: t })}>{L.typesDommage[t] ?? t}</button>
                      ))}
                    </div>
                  </Champ>
                  <Champ label={L.commentaire}>
                    <input value={nouveau.commentaire ?? ''} onChange={(e) => setNouveau({ ...nouveau, commentaire: e.target.value })} placeholder={L.commentairePlaceholder} />
                  </Champ>
                  <div className="actions">
                    <button className="btn" onClick={() => setNouveau(null)}>{L.annuler}</button>
                    <button className="btn btn-p" onClick={() => { setDommages([...dommages, nouveau]); setNouveau(null); }}>{L.ajouter}</button>
                  </div>
                </div>
              )}
              {dommages.length > 0 && (
                <ul style={{ marginTop: 12, paddingLeft: 18 }} className="mini">
                  {dommages.map((d, i) => (
                    <li key={i}>{i + 1}. {L.typesDommage[d.type] ?? d.type} — {L.zones[d.zone] ?? d.zone.replace('_', ' ')} {d.commentaire && `· ${d.commentaire}`}
                      <button className="btn btn-s btn-f" onClick={() => setDommages(dommages.filter((_, j) => j !== i))}>{L.retirer}</button>
                    </li>
                  ))}
                </ul>
              )}
            </Carte>
          )}

          {etape === 3 && (
            <Carte titre={L.carburantKmProprete}>
              <div style={{ display: 'grid', gap: 16 }}>
                <Champ label={retour ? L.dateRetour : L.dateDepart}
                       aide={L.momentAide}>
                  <input type="datetime-local" value={moment} onChange={(e) => setMoment(e.target.value)} />
                </Champ>
                {/*
                  * LE RETARD SE VOIT AVANT D'ÊTRE FACTURÉ.
                  *
                  * Un retour saisi deux jours après coup gardait l'heure de la SAISIE :
                  * 55 heures de retard, 33 000 DZD, pour un client rendu à l'heure.
                  * On montre donc ce que l'heure saisie va coûter, tant qu'on peut
                  * encore la corriger. La voiture est due à la fin prévue, ou au départ
                  * réel plus la durée réservée si elle est partie plus tard.
                  */}
                {retour && (() => {
                  const r = contrat.reservation as Reservation | string;
                  if (typeof r === 'string' || !moment) return null;
                  const fin = new Date(r.dateFin).getTime();
                  const duree = fin - new Date(r.dateDebut).getTime();
                  const depart = contrat.remise?.dateHeure ? new Date(contrat.remise.dateHeure).getTime() + duree : 0;
                  const du = Math.max(fin, depart);
                  const heures = Math.ceil((new Date(moment).getTime() - du) / 3_600_000);
                  if (heures <= 0) return null;
                  return (
                    <Bandeau type="ko" titre={L.retardTitre(heures, prixRetard ? montant(heures * prixRetard) : '')}>
                      {L.retardDetail(dateHeureFr(new Date(du).toISOString()), dateHeureFr(new Date(moment).toISOString()))}
                    </Bandeau>
                  );
                })()}
                <Champ label={L.carburant} aide={L.carburantAide}>
                  <div className="choix">
                    {[1, 2, 3, 4].map((q) => (
                      <button key={q} type="button" aria-pressed={quarts === q} onClick={() => setQuarts(q)}>{CARBURANT_LABEL[q]}</button>
                    ))}
                  </div>
                </Champ>
                <Champ label={L.kmCompteur}
                       aide={retour && depart ? L.kmAide(nb(depart.km), nb(Math.max(0, Number(km) - depart.km)), u) : undefined}>
                  <input type="number" inputMode="numeric" value={km} onChange={(e) => setKm(e.target.value)} />
                </Champ>
                <Champ label={L.propreteInt}>
                  <div className="choix">{[10, 5, 0].map((p) => (
                    <button key={p} type="button" aria-pressed={propInt === p} onClick={() => setPropInt(p)}>{p}/10</button>))}
                  </div>
                </Champ>
                <Champ label={L.propreteExt}>
                  <div className="choix">{[10, 5, 0].map((p) => (
                    <button key={p} type="button" aria-pressed={propExt === p} onClick={() => setPropExt(p)}>{p}/10</button>))}
                  </div>
                </Champ>
              </div>
            </Carte>
          )}

          {etape === 4 && (
            <Carte titre={L.accessoiresTitre}>
              <div className="accessoires">
                {ACCESSOIRES().map(([k, l]) => (
                  <label key={k}>
                    <input type="checkbox" checked={acc[k] ?? false} onChange={(e) => setAcc({ ...acc, [k]: e.target.checked })} />
                    {l}
                  </label>
                ))}
              </div>
              <div style={{ marginTop: 14 }}>
                <Champ label={L.observations}><textarea value={observations} onChange={(e) => setObservations(e.target.value)} /></Champ>
              </div>
            </Carte>
          )}

          {etape === 5 && (
            <Carte titre={L.signatures}>
              <div style={{ display: 'grid', gap: 16 }}>
                <Signature label={L.sigClientLabel} valeur={sigClient} onChange={setSigClient} />
                <Signature label={L.sigAgentLabel} valeur={sigAgent} onChange={setSigAgent} />
              </div>
              {/*
                * L'etat des lieux photographique et les deux signatures sont,
                * avec le journal, ce qui tient lieu de preuve. Un etat des lieux
                * sans signature n'engage personne : il ne doit pas pouvoir etre
                * valide par distraction.
                */}
              {(!sigClient || !sigAgent) && (
                <div style={{ marginTop: 14 }}>
                  <Bandeau type="ko" titre={L.sigManquantes}>
                    {!sigClient && !sigAgent
                      ? L.deuxManquent
                      : !sigClient ? L.clientManque : L.votreManque}
                    {' '}{L.sansElles}
                  </Bandeau>
                </div>
              )}

              {photos.length === 0 && (
                <div style={{ marginTop: 14 }}>
                  <Bandeau type="warn" titre={L.aucunePhoto}>
                    {L.aucunePhotoTexte}
                    <label style={{ display: 'flex', gap: 9, alignItems: 'center', marginTop: 10 }}>
                      <input type="checkbox" checked={sansPhotoAssume}
                             onChange={(e) => setSansPhotoAssume(e.target.checked)} />
                      {L.sansPhoto}
                    </label>
                  </Bandeau>
                </div>
              )}

              <div style={{ marginTop: 14 }}>
                <Bandeau type="warn" titre={L.plusModifier}>
                  {L.verifiez}
                </Bandeau>
              </div>
            </Carte>
          )}

          <div className="actions" style={{ justifyContent: 'space-between' }}>
            <button className="btn" disabled={etape === 1} onClick={() => setEtape(etape - 1)}>{L.retour}</button>
            {etape < 5
              ? <button className="btn btn-p btn-l" onClick={() => setEtape(etape + 1)}>{L.suivant}</button>
              : <button className="btn btn-p btn-l"
                        disabled={envoi || !km || !sigClient || !sigAgent || (photos.length === 0 && !sansPhotoAssume)}
                        onClick={() => void valider()}>
                  {envoi ? (progres || L.enregistrement) : retour ? L.validerRetour : L.validerDepart}
                </button>}
          </div>
        </div>

        <Carte titre={L.recap}>
          <table className="tbl compact">
            <tbody>
              <tr><td>{L.carburant}</td><td className="droite"><b>{CARBURANT_LABEL[quarts]}</b></td></tr>
              <tr><td>{L.kilometrage}</td><td className="droite num">{nb(Number(km || 0))} {u}</td></tr>
              <tr><td>{L.proprete}</td><td className="droite">{L.propreteValeur(propInt, propExt)}</td></tr>
              <tr><td>{L.accessoiresPresents}</td><td className="droite">{Object.values(acc).filter(Boolean).length} / 8</td></tr>
              <tr><td>{L.photosPrises}</td><td className="droite" style={{ color: photos.length >= 6 ? 'var(--succes)' : 'var(--attention)' }}>{photos.length} / 6</td></tr>
              <tr><td>{L.dommagesMarques}</td><td className="droite">{dommages.length}</td></tr>
              <tr><td>{L.signatures}</td><td className="droite">{[sigClient && L.motClient, sigAgent && L.motAgent]
.filter(Boolean).join(' + ') || '—'}</td></tr>
            </tbody>
          </table>
        </Carte>
      </div>
    </>
  );
}
